/**
 * The item card's **"…" Options** menu and its move picker (parity card **P-060**).
 *
 * monday's card header carries an 18 × 18 `…` at (1390, 117),
 * `data-testid="clickable_menuButton_*"` (`discovery/ui-reference/item-card/INDEX.md`
 * § "Card chrome"), and it is the only place the card offers rename, duplicate, move, archive
 * and delete. Ours had a close button, the name as a plain `<h1>` and the four tabs — so an
 * item could not be acted on from its card at all, and on a phone (P-062) the card *is* the
 * item.
 *
 * **The row list is ASSUMED (q491).** Only monday's *button* was captured, never the menu it
 * opens, so the rows below are the five the round-9y brief names plus `Copy item link`, which
 * the grid's row menu already offers for the same item. Nothing else is invented.
 *
 * Two things about the markup are deliberate:
 *
 *   1. it is rendered **inside** `[data-testid="item-card"]`, not portalled to `document.body`
 *      like the grid's `Menu` — the card is a panel with its own dismiss and Escape handling,
 *      and an action that belongs to this item should live in this item's subtree;
 *   2. the rows stay **mounted** and the container carries `hidden` while the menu is closed,
 *      so `hidden` (not unmounting) is what removes them from the a11y tree and from view. The
 *      `role="menu"` / `role="menuitem"` semantics are applied only while it is open, so a
 *      closed menu is not an open one to anything that counts them.
 */
import { useEffect, useRef, useState } from 'react';
import { Icon, icons } from '@elizmonday/ui';
import type { RaiseCardToast } from './CardToast';
import type { CardBoardOption, CardGroupOption, ItemCardSource } from './types';

export interface CardMenuProps {
  source: ItemCardSource;
  itemId: string;
  boardId: string;
  name: string;
  /** The group the item is in, so an undone `Move to group` puts it back where it was (P-164). */
  currentGroupId?: string | null;
  /** Starts the header's inline rename; the menu owns no input of its own. */
  onRename(): void;
  /** The item left this board (archived, deleted, moved away): the panel closes. */
  onGone(): void;
  /** Something changed that the header shows — re-read it. */
  onChanged(): void;
  /**
   * **P-164** — the card's toast host. Every action below used to complete in silence, while
   * the identical action from the grid's row menu toasted with a working **Undo**, so the
   * item's own card was the one surface where an accidental *Delete* could not be taken back.
   */
  onToast: RaiseCardToast;
  /**
   * **P-167** — whether this menu (or its move dialog) is the card's topmost layer, so the
   * card's `window` Escape listener knows not to close the whole panel underneath it.
   */
  onOpenChange(open: boolean): void;
}

export function CardMenu({
  source,
  itemId,
  boardId,
  name,
  currentGroupId = null,
  onRename,
  onGone,
  onChanged,
  onToast,
  onOpenChange,
}: CardMenuProps) {
  const [open, setOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [groups, setGroups] = useState<readonly CardGroupOption[]>([]);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  // The menu closes on a click anywhere outside it, as the grid's menus do.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      const node = root.current;
      if (node && !node.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown, true);
    return () => document.removeEventListener('mousedown', onPointerDown, true);
  }, [open]);

  useEffect(() => {
    if (!open) setGroupsOpen(false);
  }, [open]);

  // P-167: the card's Escape listener is a no-op while a layer of the menu's is up.
  useEffect(() => {
    onOpenChange(open || picking);
  }, [open, picking, onOpenChange]);

  // Escape closes this menu — one layer — and never the card behind it (F4 §P5).
  useEffect(() => {
    if (!open && !picking) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      if (picking) setPicking(false);
      else setOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, picking]);

  /**
   * One action: run it, report what went wrong, and never leave the menu open on success.
   *
   * **P-164**: and toast it, with the same wording the grid uses for the same action and the
   * same inverse call for Undo (`items.restore` after a delete, `items.unarchive` after an
   * archive, `items.move` back to the previous group after a move). `toast` is omitted only
   * where there is nothing to say — `Copy item link` and `Rename`, which the card's own header
   * toasts once the name has actually changed.
   */
  const run = async (
    action: () => Promise<void>,
    after: 'gone' | 'changed',
    toast?: { message: string; undo?: () => Promise<void> },
  ) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await action();
      setOpen(false);
      if (toast) {
        onToast({
          message: toast.message,
          kind: 'info',
          ...(toast.undo
            ? {
                actionLabel: 'Undo',
                onAction: async () => {
                  await toast.undo?.();
                  onChanged();
                },
              }
            : {}),
        });
      }
      if (after === 'gone') onGone();
      else onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That action could not be completed');
    } finally {
      setBusy(false);
    }
  };

  const openGroups = async () => {
    setGroupsOpen((current) => !current);
    if (groups.length > 0) return;
    setGroups(await source.boardGroups({ boardId }).catch(() => []));
  };

  const row = (extra?: Record<string, unknown>) => ({
    type: 'button' as const,
    className: 'ez-item-card__menu-item',
    ...(open ? { role: 'menuitem' } : {}),
    disabled: busy,
    ...extra,
  });

  return (
    <div className="ez-item-card__options" ref={root}>
      <button
        type="button"
        className="ez-item-card__options-button"
        data-testid="item-card-menu"
        aria-label={`More options for ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <Icon icon={icons.moreActions} size={18} ignoreFocusStyle />
      </button>

      <div
        className="ez-item-card__menu"
        data-testid="item-card-options"
        hidden={!open}
        {...(open ? { role: 'menu', 'aria-label': `Actions for ${name}` } : {})}
      >
        {/* P-163: every row is addressable by `card-menu-<action>`, not only by its text. */}
        <button {...row()} data-testid="card-menu-rename" onClick={() => { setOpen(false); onRename(); }}>
          Rename
        </button>
        <button
          {...row()} data-testid="card-menu-copy-link"
          onClick={() => {
            setOpen(false);
            void navigator.clipboard
              ?.writeText(`${window.location.origin}/boards/${boardId}/pulses/${itemId}`)
              .catch(() => undefined);
          }}
        >
          Copy item link
        </button>
        <hr className="ez-item-card__menu-divider" />
        <button
          {...row({ 'aria-expanded': groupsOpen })} data-testid="card-menu-move-group"
          onClick={() => void openGroups()}
        >
          Move to group
        </button>
        {groupsOpen
          ? groups.map((group) => (
              <button
                key={group.id}
                {...row({ className: 'ez-item-card__menu-item ez-item-card__menu-item--nested' })}
                data-testid={`card-menu-group-${group.id}`}
                onClick={() =>
                  void run(() => source.moveItemToGroup({ itemId, groupId: group.id }), 'changed', {
                    message: `Moved to ${group.title}`,
                    // The inverse of a move is the move back — the group it left.
                    ...(currentGroupId
                      ? { undo: () => source.moveItemToGroup({ itemId, groupId: currentGroupId }) }
                      : {}),
                  })
                }
              >
                {group.title}
              </button>
            ))
          : null}
        <button
          {...row()} data-testid="card-menu-move-board"
          onClick={() => { setOpen(false); setPicking(true); }}
        >
          Move to board
        </button>
        <button
          {...row()} data-testid="card-menu-duplicate"
          onClick={() =>
            void run(() => source.duplicateItem({ itemId }), 'changed', { message: `Duplicated "${name}"` })
          }
        >
          Duplicate
        </button>
        <hr className="ez-item-card__menu-divider" />
        <button
          {...row()} data-testid="card-menu-archive"
          onClick={() =>
            void run(() => source.archiveItem({ itemId }), 'gone', {
              message: 'Archived 1 item',
              undo: () => source.unarchiveItem({ itemId }),
            })
          }
        >
          Archive
        </button>
        <button
          {...row({ className: 'ez-item-card__menu-item ez-item-card__menu-item--danger' })}
          data-testid="card-menu-delete"
          onClick={() =>
            void run(() => source.deleteItem({ itemId }), 'gone', {
              message: 'Deleted 1 item',
              undo: () => source.restoreItem({ itemId }),
            })
          }
        >
          Delete
        </button>
      </div>

      {error ? (
        <p className="ez-item-card__menu-error" role="alert" data-testid="item-card-menu-error">
          {error}
        </p>
      ) : null}

      {picking ? (
        <MoveToBoardDialog
          source={source}
          itemId={itemId}
          onClose={() => setPicking(false)}
          onMoved={(destination) => {
            setPicking(false);
            // P-164: a move off the board closes the card, so the toast is the only trace of it.
            onToast({ message: `Moved to ${destination}`, kind: 'info' });
            onGone();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * `Move to board` needs a destination **board and group**, because `items.moveToBoard` takes
 * both — the same two-step picker the grid's row menu opens, drawn inside the card because the
 * card is not mounted inside the board view's store.
 */
function MoveToBoardDialog({
  source,
  itemId,
  onClose,
  onMoved,
}: {
  source: ItemCardSource;
  itemId: string;
  onClose(): void;
  onMoved(destination: string): void;
}) {
  const [boards, setBoards] = useState<readonly CardBoardOption[]>([]);
  const [groups, setGroups] = useState<readonly CardGroupOption[]>([]);
  const [boardId, setBoardId] = useState<string>('');
  const [groupId, setGroupId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void source
      .boards()
      .then(setBoards)
      .catch(() => setBoards([]));
  }, [source]);

  useEffect(() => {
    setGroupId('');
    if (boardId === '') {
      setGroups([]);
      return;
    }
    void source
      .boardGroups({ boardId })
      .then(setGroups)
      .catch(() => setGroups([]));
  }, [source, boardId]);

  const move = async () => {
    if (boardId === '' || groupId === '') return;
    setBusy(true);
    setError(null);
    try {
      await source.moveItemToBoard({ itemId, boardId, groupId });
      onMoved(boards.find((board) => board.id === boardId)?.name ?? 'another board');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That item could not be moved');
      setBusy(false);
    }
  };

  return (
    <div className="ez-item-card__move" role="dialog" aria-label="Move to board" data-testid="item-card-move-dialog">
      <label className="ez-item-card__move-field">
        <span>Board</span>
        <select
          data-testid="item-card-move-board"
          value={boardId}
          onChange={(event) => setBoardId(event.target.value)}
        >
          <option value="">Choose a board…</option>
          {boards.map((board) => (
            <option key={board.id} value={board.id}>
              {board.name}
            </option>
          ))}
        </select>
      </label>
      <label className="ez-item-card__move-field">
        <span>Group</span>
        <select
          data-testid="item-card-move-group"
          value={groupId}
          disabled={groups.length === 0}
          onChange={(event) => setGroupId(event.target.value)}
        >
          <option value="">Choose a group…</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.title}
            </option>
          ))}
        </select>
      </label>
      {error ? <p role="alert">{error}</p> : null}
      <div className="ez-item-card__move-actions">
        <button type="button" data-testid="item-card-move-cancel" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          data-testid="item-card-move-submit"
          disabled={busy || boardId === '' || groupId === ''}
          onClick={() => void move()}
        >
          Move
        </button>
      </div>
    </div>
  );
}
